/**
 * ManualVacancyPanel Component
 *
 * Displays details of a manually uploaded vacancy
 */

import React, { useState, useEffect } from 'react';
import { api, APIError } from '../../api/client';

/**
 * @param {Object} props
 * @param {string} props.vacancyId - ID of the vacancy to load
 * @param {string} [props.fallbackTitle] - Title to show if vacancy has no title
 * @param {Function} [props.onUnauthorized] - Callback when user is unauthorized
 */
export default function ManualVacancyPanel({ vacancyId, fallbackTitle, onUnauthorized }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [vacancy, setVacancy] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');

      try {
        const data = await api.get(`/api/vacancies/${vacancyId}`);
        setVacancy(data);
      } catch (err) {
        if (err instanceof APIError) {
          if (err.status === 401 || err.status === 403) {
            onUnauthorized?.();
            return;
          }
          setError(err.message || 'Failed to load vacancy details.');
        } else {
          console.error('[ManualVacancyPanel] Failed to load vacancy:', err);
          setError('Failed to load vacancy details.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (vacancyId) {
      load();
    }
  }, [vacancyId, onUnauthorized]);

  if (loading) {
    return (
      <div className="bg-white rounded-[24px] p-6 text-center text-gray-500 shadow-sm">
        Loading vacancy...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-2xl text-red-700 text-sm">
        {error}
      </div>
    );
  }

  const title = vacancy?.title || fallbackTitle || 'Uploaded vacancy';

  return (
    <section className="bg-white rounded-[32px] p-6 md:p-8 shadow-sm space-y-4">
      <div className="flex items-start justify-between gap-6">
        <div>
          <h2 className="text-xl font-semibold">{title}</h2>
          <p className="text-xs text-gray-400 mt-2 break-all">{vacancyId}</p>
        </div>
        <span className="px-3 py-1 rounded-full border border-orange-200 bg-orange-50 text-xs font-semibold text-orange-600">
          Manual
        </span>
      </div>

      <div className="bg-[#F6F7FB] rounded-2xl p-5">
        <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
          {vacancy?.text || ''}
        </div>
      </div>
    </section>
  );
}
